"use client";

import { useState } from "react";
import { Star, User, TrendingUp, Eye } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../UI/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../UI/tabs";
import { Badge } from "../UI/badge";
import Pagination from "../../utils/pagination";


const categoryLabels = {
  avg_checkin: "Checkin",
  avg_cleanliness: "Limpieza",
  avg_communication: "Comunicación",
  avg_location: "Ubicación",
  avg_price_quality: "Precio según calidad",
  avg_truthfulness: "Veracidad",
};

export default function ReviewsOverview({
  statistics,
  data,
  properties,
  setFilter,
  filter,
}: any) {
  const [selectedAccommodation, setSelectedAccommodation] = useState("all");
  const [activeTab, setActiveTab] = useState("evaluaciones");

  const totalRating = (reviews: any) => {
    let total = 0;

    total =
      reviews.checkin +
      reviews.cleanliness +
      reviews.communication +
      reviews.location +
      reviews.price_quality +
      reviews.truthgulness;

    return total / 6;
  };

  const averageRating = (statistic: any) => {
    let total = 0;

    total =
      statistic.avg_checkin +
      statistic.avg_cleanliness +
      statistic.avg_communication +
      statistic.avg_location +
      statistic.avg_price_quality +
      statistic.avg_truthfulness;

    return (total / 6).toFixed(2);
  };

  const StarRating = ({
    rating,
    size = "w-4 h-4",
  }: {
    rating: number;
    size?: string;
  }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`${size} ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-300"
            }`}
          />
        ))}
      </div>
    );
  };

  const CategoryBar = ({
    label,
    rating,
  }: {
    label: string;
    rating: number;
  }) => {
    const percentage = (rating / 5) * 100;

    return (
      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">{label}</span>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">{rating}</span>
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
          </div>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="evaluaciones">Evaluaciones</TabsTrigger>
          <TabsTrigger className="hidden" value="ganancias">
            Ganancias
          </TabsTrigger>
          <TabsTrigger className="hidden" value="visitas">
            Visitas
          </TabsTrigger>
        </TabsList>

        <TabsContent value="evaluaciones" className="space-y-6">
          {/* Filter Dropdown */}
          <div className="w-full max-w-sm">
            <Select
              value={selectedAccommodation}
              onValueChange={setSelectedAccommodation}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {properties.map((accommodation: any) => (
                  <SelectItem key={accommodation.id} value={accommodation.id}>
                    {accommodation.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Calificaciones general
                </h3>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <span className="text-3xl font-bold">
                    {averageRating(statistics)}
                  </span>
                  <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                </div>
                <StarRating
                  rating={Number(averageRating(statistics))}
                  size="w-5 h-5"
                />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Todas las evaluaciones
                </h3>
                <div className="text-3xl font-bold text-gray-900">
                  {statistics?.total_reviews}
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  evaluaciones totales
                </p>
              </div>
            </div>
          </div>

          {/* Category Ratings */}
          <div className="bg-white p-6 rounded-lg border shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Calificaciones por categoría
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries({
                avg_checkin: statistics.avg_checkin,
                avg_cleanliness: statistics.avg_cleanliness,
                avg_communication: statistics.avg_communication,
                avg_location: statistics.avg_location,
                avg_price_quality: statistics.avg_price_quality,
                avg_truthfulness: statistics.avg_truthfulness,
              }).map(([key, rating]) => (
                <CategoryBar
                  key={key}
                  label={categoryLabels[key as keyof typeof categoryLabels]}
                  rating={rating}
                />
              ))}
            </div>
          </div>

          {/* Reviews List */}
          <div className="bg-white rounded-lg border shadow-sm">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {statistics?.total_reviews} Evaluaciones
              </h3>
            </div>
            <div className="divide-y">
              {data.data.map((review: any) => (
                <div key={review.id} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-gray-600" />
                    </div>
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {`${review.user[0]?.firstname} ${review.user[0]?.lastname}`}
                          </h4>
                          <p className="text-sm text-gray-600">{review.date}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <StarRating rating={totalRating(review)} />
                          <Badge variant="outline" className="text-xs">
                            {}
                          </Badge>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700">
                          Alojamiento: {review.property}
                        </p>
                        <p className="text-gray-700 leading-relaxed">
                          {review.description}
                        </p>
                      </div>

                      {/* Detailed Category Ratings */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-3 border-t">
                        {Object.entries({
                          avg_checkin: review.checkin,
                          avg_cleanliness: review.cleanliness,
                          avg_communication: review.communication,
                          avg_location: review.location,
                          avg_price_quality: review.price_quality,
                          avg_truthfulness: review.truthfulness,
                        }).map(([key, rating]) => (
                          <div
                            key={key}
                            className="flex items-center justify-between text-xs"
                          >
                            <span className="text-gray-600">
                              {
                                categoryLabels[
                                  key as keyof typeof categoryLabels
                                ]
                              }
                            </span>
                            <div className="flex items-center gap-1">
                              <span className="font-medium">{rating}</span>
                              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <Pagination data={data} setPage={setFilter} page={filter} />
          </div>
        </TabsContent>

        <TabsContent value="ganancias" className="space-y-6">
          <div className="bg-white p-8 rounded-lg border shadow-sm text-center">
            <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Vista de Ganancias
            </h3>
            <p className="text-gray-600">
              Esta sección mostrará el análisis detallado de ganancias por
              alojamiento.
            </p>
          </div>
        </TabsContent>

        <TabsContent value="visitas" className="space-y-6">
          <div className="bg-white p-8 rounded-lg border shadow-sm text-center">
            <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Vista de Visitas
            </h3>
            <p className="text-gray-600">
              Esta sección mostrará las estadísticas de visitas y
              visualizaciones.
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
